import { useState, type SyntheticEvent, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { ApiError, authApi } from '@invite/app-core';
import { AuthLayout } from '../app/AuthLayout';
import { useT } from '../i18n/useT';
import { twoFactorSession } from './twoFactorSession';

export const TwoFactorConfigureScreen = (): JSX.Element => {
  const t = useT();
  const navigate = useNavigate();
  // Captured once: a later clear() (on success) must not bounce this screen back to login.
  const [session] = useState(() => twoFactorSession.get());
  const [phone, setPhone] = useState(session?.phone ?? '');
  const [error, setError] = useState<string | null>(null);

  if (session === null) {
    return <Navigate to="/login" replace />;
  }

  const configure = async (method: 'app' | 'sms'): Promise<void> => {
    setError(null);
    if (method === 'sms' && phone.trim().length === 0) {
      setError(t('Phone number is required for SMS verification.'));
      return;
    }
    try {
      const result = await authApi.configureTwoFactor(session.token, {
        method,
        phone: method === 'sms' ? phone : undefined
      });
      twoFactorSession.update({
        verificationType: method,
        phone: method === 'sms' ? phone : session.phone,
        config: {
          isSmsFallback: result.isSmsFallback ?? method === 'sms',
          phone: result.phone ?? (method === 'sms' ? phone : null),
          qrUrl: result.qrUrl ?? null,
          secretKey: result.secretKey ?? null,
          recoveryCodes: result.recoveryCodes
        }
      });
      await navigate('/login/two-factor/verify');
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : t('Unable to sign in right now. Please try again.')
      );
    }
  };

  const onApp = (event: SyntheticEvent): void => {
    event.preventDefault();
    void configure('app');
  };
  const onSms = (event: SyntheticEvent): void => {
    event.preventDefault();
    void configure('sms');
  };

  return (
    <AuthLayout>
      <h1 className="ow-heading">{t('Set up two-factor authentication')}</h1>
      <p className="ow-muted">{t('Choose how you want to verify your sign-in.')}</p>
      {error !== null ? (
        <p className="ow-error" role="alert">
          {error}
        </p>
      ) : null}
      <form className="ow-form" onSubmit={onApp}>
        <button type="submit" className="ow-btn-secondary">
          {t('Use an authenticator app')}
        </button>
      </form>
      <form className="ow-form" onSubmit={onSms}>
        <label className="ow-label">
          {t('Phone number')}
          <input
            type="tel"
            name="phone"
            className="ow-field"
            value={phone}
            onChange={(e) => {
              setPhone(e.target.value);
            }}
            autoComplete="tel"
            placeholder={t('+47 000 00 000')}
            required
          />
        </label>
        <button type="submit" className="ow-btn-primary">
          {t('Send code by SMS')}
        </button>
      </form>
    </AuthLayout>
  );
};
